import { useCallback, useContext, useEffect, useState } from 'react';
import { useApi } from '@shared/services/api.js';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext.js';
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest.js';

interface ProfileUser {
  avatarColor?: string;
  company?: string;
  email: string;
  firstName?: string;
  id: number;
  lastName?: string;
  positionTitle?: string;
  showDealList?: boolean;
  tagLine?: string;
  username?: string;
  userPreference?: ProfileUserPreferences;
}

interface ProfileUserPreferences {
  showDealList?: boolean;
}

interface ReactionStats {
  loading: boolean;
  totalDislikes: number;
  totalLikes: number;
}

interface Review {
  companyId?: number;
  companyName: string;
  companyUrl?: string;
  createdAt: string;
  dealArea: string;
  dealClose: string;
  dealSize: string;
  dealType: string;
  id: number;
  likeDislikeCount?: number;
  likePercentage?: number;
  userId?: number;
  viewed?: boolean;
}

interface UseUserProfileDataReturn {
  consultationRequests: IntelligenceRequest[];
  loading: boolean;
  myRequestsToThisUser: IntelligenceRequest[];
  outgoingRequests: IntelligenceRequest[];
  profileUser: null | ProfileUser;
  reactionStats: ReactionStats;
  refetchUserData: () => Promise<void>;
  reviews: Review[];
  userNotFound: boolean;
}

export const useUserProfileData = (userName: string | undefined): UseUserProfileDataReturn => {
  const { user: loggedInUser, userLoadState } = useContext(UserContext);
  const api = useApi();

  const [profileUser, setProfileUser] = useState<null | ProfileUser>(null);
  const [userNotFound, setUserNotFound] = useState(false);
  const [consultationRequests, setConsultationRequests] = useState<IntelligenceRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<IntelligenceRequest[]>([]);
  const [myRequestsToThisUser, setMyRequestsToThisUser] = useState<IntelligenceRequest[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [reactionStats, setReactionStats] = useState<ReactionStats>({
    loading: true,
    totalDislikes: 0,
    totalLikes: 0,
  });

  const fetchUserData = useCallback(async () => {
    if (!userName) {
      return;
    }

    if (userLoadState === UserLoadState.None) {
      return;
    }

    try {
      setLoading(true);
      setUserNotFound(false);

      const profileResponse = await api.get(`/user/profile/${userName}`);

      if (profileResponse?.error) {
        setUserNotFound(true);
        setLoading(false);
        return;
      }

      setProfileUser({
        avatarColor: profileResponse.avatarColor,
        company: profileResponse.company,
        email: '',
        firstName: profileResponse.firstName,
        id: profileResponse.id,
        lastName: profileResponse.lastName,
        positionTitle: profileResponse.positionTitle,
        showDealList: profileResponse.userPreference?.showDealList ?? true,
        tagLine: profileResponse.tagLine,
        username: profileResponse.username,
        userPreference: profileResponse.userPreference,
      });

      if (profileResponse.intelligenceRequests) {
        setConsultationRequests(profileResponse.intelligenceRequests);
      }

      if (profileResponse.outgoingIntelligenceRequests) {
        setOutgoingRequests(profileResponse.outgoingIntelligenceRequests);
      }

      const isOwnProfile = loggedInUser?.id?.toString() === profileResponse.id.toString();
      if (!isOwnProfile && loggedInUser?.id) {
        const myRequestsResponse = await api.get('/intelligence-request', {
          params: {
            authorId: profileResponse.id,
            limit: 100,
            userId: loggedInUser.id,
          },
        });

        if (!myRequestsResponse?.error && myRequestsResponse?.data) {
          setMyRequestsToThisUser(myRequestsResponse.data);
        }
      } else {
        setMyRequestsToThisUser([]);
      }

      if (profileResponse.reviews) {
        setReviews(profileResponse.reviews);

        let totalLikes = 0;
        let totalDislikes = 0;

        profileResponse.reviews.forEach((review: Review) => {
          if (review.likeDislikeCount && review.likePercentage !== undefined) {
            const likes = Math.round(review.likeDislikeCount * review.likePercentage);
            const dislikes = review.likeDislikeCount - likes;

            totalLikes += likes;
            totalDislikes += dislikes;
          }
        });

        setReactionStats({
          loading: false,
          totalDislikes,
          totalLikes,
        });
      }
    } catch {
      setUserNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [userName, loggedInUser?.id, userLoadState]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return {
    consultationRequests,
    loading,
    myRequestsToThisUser,
    outgoingRequests,
    profileUser,
    reactionStats,
    refetchUserData: fetchUserData,
    reviews,
    userNotFound,
  };
};
